import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { createAppAsyncThunk } from "../../app/thunkWithTypes";
import { createUser, getUser } from "../../api/client";
import { instanceToPlain } from "class-transformer";
import { selectAuthServerUser, selectAuthServerUserId } from "../authentication/authentication-slice";

interface UserSliceState {
  fetchUserStatus: 'idle' | 'pending' | 'succeeded' | 'failed'
  user: object | undefined
  error: string | null
}

const initialState: UserSliceState = {
  fetchUserStatus: 'idle',
  user: undefined,
  error: null
}

export const fetchUser = createAppAsyncThunk('user/fetchUser', async (accessToken: string, thunkApi) => {
  const user = await getUser(accessToken);
  if (user) {
    return Object(instanceToPlain(user));
  } else {
    const authServerUserId = selectAuthServerUserId(thunkApi.getState());
    const authServerUser = selectAuthServerUser(thunkApi.getState());
    if (!authServerUser.email) {
      throw new Error('Trying to create a user without email.');
    }
    if (!authServerUserId) {
      throw new Error('Trying to create a user without an authorization server user id.');
    }
    const alternativeName = authServerUser.email?.split('@')[0].replace(/\./g, ' ');
    const name = (authServerUser.name !== authServerUser.email) ? authServerUser.name : alternativeName;
    const newUserId = await createUser(accessToken, authServerUserId, name ?? '', authServerUser.email);
    if (newUserId) {
      console.log(`Created new user successfully. User email: ${authServerUser.email}, user ID: ${newUserId}.`)
    }
    const newUser = await getUser(accessToken);
    return Object(instanceToPlain(newUser));
  }
}, {
  condition(arg, thunkApi) {
    const postsStatus = selectFetchUserStatus(thunkApi.getState())
    if (postsStatus !== 'idle') {
      return false;
    }
  }
})

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<object | undefined>) {
      state.user = action.payload;
    }
  }, extraReducers: builder => {
    builder
      .addCase(fetchUser.pending, (state, action) => {
        state.fetchUserStatus = 'pending';
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.fetchUserStatus = 'succeeded';
        state.user = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.fetchUserStatus = 'failed';
        state.error = action.error.message ?? 'Unknown Error';
      })
  }
})

// Export selectors
export const selectUser = (state: RootState) => state.user.user;
export const selectFetchUserStatus = (state: RootState) => state.user.fetchUserStatus;

// Export the auto-generated action creator with the same name
export const { setUser } = userSlice.actions;

// Export the generated reducer function
export default userSlice.reducer;
